import { getAnalytics } from "../../../api/admin";
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Grid,
  Typography,
  Paper,
  Container,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useTheme } from "@mui/material/styles";
import { Skeleton } from "@mui/material";
import { Helmet } from "react-helmet-async";

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        const fetchedAnalytics = await getAnalytics();
        setData(fetchedAnalytics);
        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch analytics:", error);
      }
    };

    // Fetch auctions initially
    fetchAnalytics();
  }, []);

  // Bar chart data preparation
  const auctionStatusData = data
    ? [
        { name: "Neaktīvs", value: data.total_inactive_auctions },
        { name: "Gaida Apmaksu", value: data.total_pending_auctions },
        { name: "Aktīvs", value: data.total_active_auctions },
        { name: "Pabeigts", value: data.total_completed_auctions },
      ]
    : [];

  const userTypeData = data
    ? [
        { name: "Uzņēmums", value: data.total_business_users },
        { name: "Privātpersona", value: data.total_private_users },
      ]
    : [];

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Container maxWidth="lg">
        <Box sx={{ p: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Pārskats
          </Typography>

          <Grid container spacing={4}>
            {/* User Cards */}
            <Grid item xs={12} sm={6} md={3}>
              {" "}
              {/* Adjusted md value */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="p"
                    color="textSecondary"
                    gutterBottom
                  >
                    Lietotāji
                  </Typography>
                  <Typography
                    variant="body"
                    component="p"
                    sx={{ fontSize: 26, fontWeight: "regular" }}
                  >
                    {loading ? <Skeleton width="60%" /> : data?.total_users}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {" "}
              {/* Adjusted md value */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="p"
                    color="textSecondary"
                    gutterBottom
                  >
                    Uzņēmumi
                  </Typography>
                  <Typography
                    variant="body"
                    component="p"
                    sx={{ fontSize: 26, fontWeight: "regular" }}
                  >
                    {loading ? (
                      <Skeleton width="60%" />
                    ) : (
                      data?.total_business_users
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {" "}
              {/* Adjusted md value */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="p"
                    color="textSecondary"
                    gutterBottom
                  >
                    Privātpersonas
                  </Typography>
                  <Typography
                    variant="body"
                    component="p"
                    sx={{ fontSize: 26, fontWeight: "regular" }}
                  >
                    {loading ? (
                      <Skeleton width="60%" />
                    ) : (
                      data?.total_private_users
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Auction Cards */}
            <Grid item xs={12} sm={6} md={3}>
              {" "}
              {/* Adjusted md value */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="p"
                    color="textSecondary"
                    gutterBottom
                  >
                    Izsoles
                  </Typography>
                  <Typography
                    variant="body"
                    component="p"
                    sx={{ fontSize: 26, fontWeight: "regular" }}
                  >
                    {loading ? <Skeleton width="60%" /> : data?.total_auctions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              {" "}
              {/* Adjusted md value */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="p"
                    color="textSecondary"
                    gutterBottom
                  >
                    Aktīvas Izsoles
                  </Typography>
                  <Typography
                    variant="body"
                    component="p"
                    sx={{ fontSize: 26, fontWeight: "regular" }}
                  >
                    {loading ? (
                      <Skeleton width="60%" />
                    ) : (
                      data?.total_active_auctions
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              {" "}
              {/* Adjusted md value */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="p"
                    color="textSecondary"
                    gutterBottom
                  >
                    Solījumi
                  </Typography>
                  <Typography
                    variant="body"
                    component="p"
                    sx={{ fontSize: 26, fontWeight: "regular" }}
                  >
                    {loading ? <Skeleton width="60%" /> : data?.total_bids}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Paid Card */}
            <Grid item xs={12} sm={6} md={3}>
              {" "}
              {/* Adjusted md value */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="p"
                    color="textSecondary"
                    gutterBottom
                  >
                    Apmaksāti Rēķini
                  </Typography>
                  <Typography
                    variant="body"
                    component="p"
                    sx={{ fontSize: 26, fontWeight: "regular" }}
                  >
                    {loading ? <Skeleton width="60%" /> : data?.total_paid_fees}{" "}
                    €
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Total Pending Card */}
            <Grid item xs={12} sm={6} md={3}>
              {" "}
              {/* Adjusted md value */}
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    component="p"
                    color="textSecondary"
                    gutterBottom
                  >
                    Gaida Apmaksu
                  </Typography>
                  <Typography
                    variant="body"
                    component="p"
                    sx={{ fontSize: 26, fontWeight: "regular" }}
                  >
                    {loading ? (
                      <Skeleton width="60%" />
                    ) : (
                      data?.total_pending_fees
                    )}{" "}
                    €
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Auction Status Bar Chart */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" component="p" gutterBottom>
                  Izsoles Status
                </Typography>
                {loading ? (
                  <Skeleton variant="rectangular" width="100%" height={300} />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={auctionStatusData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      {/* <Legend /> */}
                      <Bar dataKey="value" fill={theme.palette.primary.main} />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>

            {/* User Type Bar Chart */}
            <Grid item xs={12} lg={6}>
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" component="p" gutterBottom>
                  Lietotāju Tipi
                </Typography>
                {loading ? (
                  <Skeleton variant="rectangular" width="100%" height={300} />
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart
                      data={userTypeData}
                      margin={{
                        top: 5,
                        right: 30,
                        left: 20,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <Tooltip />
                      {/* <Legend /> */}
                      <Bar
                        dataKey="value"
                        fill={theme.palette.secondary.main}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default Dashboard;
